import React from 'react'
import { useTranslation } from 'react-i18next'
import type { ReservationFormProps } from 'types/applicant'
import { BillingStatusLabels } from 'types/common'
import { StackItem } from 'ui/layout/StackItem'
import Section from 'ui/layout/Section'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { ThumbnailList } from 'ui/utils/Thumbnails'
import type { BankTransfer, Billing, ReservationDetailProps } from 'types/med'
import { SettlementsNames } from 'types/med'
import { Text1 } from 'ui/shorthands/Texts'
import { format } from 'date-fns'
import { parseDateString } from 'ui/utils/dateFormatUtils'
import { YearDateSlush } from 'config/format'
import StripeLink from 'ui/partials/StripeLink'
import BankInfo from 'ui/partials/BankInfo'

const useStyles = makeStyles()((theme: Theme) => ({
  preWrap: {
    whiteSpace: 'pre-wrap',
  },
  attention: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
  },
  stripeLink: {
    display: 'block',
    paddingTop: theme.spacing(1.25),
  },
}))

const BillingSection: React.FC<{
  reservation: ReservationDetailProps | ReservationFormProps
  billings?: Billing[]
  bankTransfer?: BankTransfer
  type: 'personal' | 'medical'
}> = ({ reservation, billings, bankTransfer, type }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const billing: Billing | undefined =
    billings && billings.length ? billings[0] : undefined

  const amount = billing && (
    <StackItem label={t('請求金額')}>
      <Text1>
        {billing?.amount.toLocaleString('ja')}
        {t('円')}
      </Text1>
    </StackItem>
  )

  const detail = billing && (
    <>
      <StackItem label={t('明細')} alignTop>
        <div className={classes.preWrap}>
          <Text1>{billing?.detail}</Text1>
        </div>
      </StackItem>
      {!!billing?.attachment_images?.length && (
        <StackItem>
          <ThumbnailList images={billing?.attachment_images} />
        </StackItem>
      )}
    </>
  )

  const billingDate = billing?.billing_date && (
    <StackItem label={t('請求日')} alignTop>
      <Text1>
        {format(parseDateString(billing.billing_date), YearDateSlush)}
        {billings && billings.length > 1 && `（${t('再送信')}）`}
      </Text1>
    </StackItem>
  )

  if (type === 'personal') {
    return (
      <Section>
        <StackItem label={t('決済ステータス')} alignTop>
          {reservation.settlement_method && (
            <Text1>
              {t(SettlementsNames[reservation.settlement_method])}
              {reservation.settlement_method !== 'no_charge' && (
                <>:&nbsp; {t('請求あり')}</>
              )}
              {/*NOTE: 受診者では決済あり、請求ありの時しか表示されない*/}
            </Text1>
          )}
        </StackItem>
        {billingDate}
        {amount}
        {reservation.settlement_method === 'bank_transfer' && bankTransfer && (
          <StackItem label={t('振込先')} alignTop>
            <BankInfo bankTransfer={bankTransfer} />
          </StackItem>
        )}
        <Text1>{detail}</Text1>
      </Section>
    )
  }

  return (
    <Section>
      <>
        <StackItem label={t('決済方法')} alignTop>
          {reservation.settlement_method ? (
            <Text1>
              {t(SettlementsNames[reservation.settlement_method])}
              :&nbsp;
              {billing?.error_type ? ( // NOTE: 現状では実質Stripeエラーの時のみ
                <span className={classes.attention}>
                  {
                    billing?.error_type === 'email_send_error' // NOTE: Stripe決済は完了しているがメール送信のみできていない状態
                      ? t(
                          'errors.決済は完了しましたがメール送信ができませんでした。申込者に請求内容をご連絡ください。'
                        )
                      : t('errors.請求エラー') // NOTE: 決済処理とメール送信を完了したがあとでStripeでエラーが出た時など
                  }
                </span>
              ) : (
                reservation.settlement_status &&
                BillingStatusLabels[reservation.settlement_status] &&
                t(BillingStatusLabels[reservation.settlement_status])
              )}
            </Text1>
          ) : (
            <Text1>{t('なし')}</Text1>
          )}
          {reservation.settlement_method === 'credit_card' && (
            <Text1 className={classes.stripeLink}>
              <StripeLink />
            </Text1>
          )}
        </StackItem>
        {billingDate}
      </>
      {reservation.settlement_method === 'bank_transfer' && bankTransfer && (
        <StackItem label={t('振込先')} alignTop>
          <BankInfo bankTransfer={bankTransfer} />
        </StackItem>
      )}
      {amount}
      {detail}
    </Section>
  )
}

BillingSection.defaultProps = {
  type: 'medical',
}

export default BillingSection
